import React, { useState } from "react";
import { toast } from "react-toastify";
import { contactService } from "../../services/api/contactService";
import { 

  WhiteBackgroundWrapper, 
  ContentWrapper, 
  ContactSection, 
  ContactForm, 
  ContactDetails 
} from "./ContactUs.styles";
import { PageContainer } from "../../components/common/PageContainer/PageContainer.styles";

const ContactUs: React.FC = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill out all required fields.");
      return;
    }

    setLoading(true);

    try {
      // Check if API endpoint is configured
      if (!import.meta.env.VITE_API_ENDPOINT) {
        toast.error('Service temporarily unavailable. Please try again later.');
        return;
      }

      await contactService.createContact(formData);
      toast.success("Your message has been sent successfully!");

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <WhiteBackgroundWrapper>
        <ContentWrapper>
          <ContactSection>
            <h1>Contact Us</h1>
            <p>
              At Hope Beyond International, we believe in ending health-related suffering through compassionate care, education, community support, and spiritual hope.
              Whether you have questions about our donation process, need guidance on patient payments, want to learn more about upcoming events, or explore volunteer
              opportunities, we’re here to help.
            </p>

            <p>
              Please fill out the form below and our dedicated team will respond as soon as possible. Your voice matters, and by connecting with us, you help us continually
              improve our services and outreach efforts.
            </p>

            <ContactForm onSubmit={handleSubmit}>
              <label htmlFor="name">Name <span aria-hidden="true">*</span></label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                required 
                placeholder="Your Full Name" 
                value={formData.name} 
                onChange={handleChange} 
              />

              <label htmlFor="email">Email <span aria-hidden="true">*</span></label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                required 
                placeholder="you@example.com" 
                value={formData.email} 
                onChange={handleChange} 
              />

              <label htmlFor="subject">Subject / Inquiry Type <span aria-hidden="true">*</span></label>
              <select 
                id="subject" 
                name="subject" 
                required 
                value={formData.subject} 
                onChange={handleChange}
              >
                <option value="" disabled>Select an option...</option>
                <option value="general">General Inquiry</option>
                <option value="donations">Donations</option>
                <option value="volunteer">Volunteer Opportunities</option>
                <option value="patient-services">Patient Services</option>
                <option value="events">Events</option>
                <option value="resources">Resources & Education</option>
              </select>

              <label htmlFor="message">Message <span aria-hidden="true">*</span></label>
              <textarea 
                id="message" 
                name="message" 
                rows={6} 
                required 
                placeholder="How can we help you?" 
                value={formData.message} 
                onChange={handleChange} 
              />

              <button type="submit" disabled={loading}>
                {loading ? "Sending..." : "Send Message"}
              </button>
            </ContactForm>

            <ContactDetails>
              <h2>Other Ways to Reach Us</h2>
              <p><strong>Email:</strong> info@hbinternational.org</p>
              <p><strong>Phone:</strong> +1 (555) 123-4567</p>
              <p>
                We value every inquiry and look forward to connecting with you. Whether you’re seeking help for a loved one, interested in contributing your time or resources,
                or simply want to learn more about our mission, Hope Beyond International is committed to listening and responding.
              </p>
            </ContactDetails>
          </ContactSection>
        </ContentWrapper>
      </WhiteBackgroundWrapper>
    </PageContainer>
  );
};

export default ContactUs;
